import { useState, useCallback } from 'react';
import { useConfigurationContext } from '../contexts/ContextoConfiguracion';
import { cajasService } from '../services/api/cajas.service';
import type { Caja, CreateCajaDTO, UpdateCajaDTO } from '../services/api/cajas.service';

export function useCajas() {
  const { state, dispatch } = useConfigurationContext();
  const [esCargando, setIsFetching] = useState(false);
  const [esGuardando, setIsSaving] = useState(false);
  const [esEliminando, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cajas = state.cajas || [];

  const cargarCajas = useCallback(
    async (search?: string, estado?: boolean, establecimientoId?: string) => {
      try {
        setIsFetching(true);
        setError(null);
        const response = await cajasService.getAll({ search, establecimientoId });

        if (!response.ok || !response.data) {
          throw new Error('Error al cargar cajas');
        }

        let filtered: Caja[] = [];
        const responseData = response.data as { data?: Caja[] } | Caja[];
        if (responseData && 'data' in responseData && Array.isArray(responseData.data)) {
          filtered = responseData.data.filter((c: Caja) => estado === undefined || c.esHabilitada === estado);
        } else if (Array.isArray(responseData)) {
          filtered = responseData.filter((c: Caja) => estado === undefined || c.esHabilitada === estado);
        }

        dispatch({ type: 'SET_CAJAS', payload: filtered });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error desconocido al cargar cajas';
        setError(errorMessage);
      } finally {
        setIsFetching(false);
      }
    },
    [dispatch]
  );

  const crearCaja = useCallback(
    async (data: Partial<Caja>): Promise<Caja | null> => {
      try {
        setIsSaving(true);
        setError(null);
        const createData: CreateCajaDTO = {
          nombre: data.nombre || '',
          observacion: data.observacion,
          monedaCodigo: data.monedaCodigo,
          monedaNombre: data.monedaNombre,
          establecimientoId: data.establecimientoId || '',
          establecimientoCodigo: data.establecimientoCodigo,
          establecimientoNombre: data.establecimientoNombre,
          medioPagos: data.medioPagos || [],
          esHabilitada: data.esHabilitada,
          limiteMaximo: data.limiteMaximo,
          margenDescuadre: data.margenDescuadre,
          tipoMargen: data.tipoMargen,
          esDefecto: data.esDefecto,
        };

        const response = await cajasService.create(createData);

        if (!response.ok || !response.data) {
          throw new Error('Error al crear caja');
        }

        await cargarCajas();
        return response.data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al crear caja';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [cargarCajas]
  );

  const actualizarCaja = useCallback(
    async (id: string, data: Partial<Caja>): Promise<Caja | null> => {
      try {
        setIsSaving(true);
        setError(null);
        const updateData: UpdateCajaDTO = {
          id,
          ...data,
        };

        const response = await cajasService.update(updateData);

        if (!response.ok || !response.data) {
          throw new Error('Error al actualizar caja');
        }

        await cargarCajas();
        return response.data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al actualizar caja';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [cargarCajas]
  );

  const eliminarCaja = useCallback(
    async (id: string): Promise<void> => {
      try {
        setIsDeleting(true);
        setError(null);
        const response = await cajasService.delete(id);

        if (!response.ok) {
          throw new Error('Error al eliminar caja');
        }

        await cargarCajas();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al eliminar caja';
        setError(errorMessage);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [cargarCajas]
  );

  const toggleStatus = useCallback(
    async (id: string): Promise<void> => {
      try {
        setIsSaving(true);
        setError(null);

        const response = await cajasService.toggleStatus(id);

        if (!response.ok || !response.data) {
          throw new Error('Error al cambiar el estado de la caja');
        }

        await cargarCajas();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al cambiar el estado';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [cargarCajas]
  );

  return {
    cajas,
    esGuardando,
    esEliminando,
    esCargando: esCargando || esGuardando || esEliminando,
    error,
    cargarCajas,
    crearCaja,
    actualizarCaja,
    eliminarCaja,
    toggleStatus,
  };
}
